import React, { useCallback, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';

const RR_API_KEY = process.env.REACT_APP_RR_LOG_ROCKET_APP_ID;
const SSR_API_KEY = process.env.REACT_APP_SSR_LOG_ROCKET_APP_ID;
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 * }>}
 */
export const LogRocketContext = React.createContext();

export function LogRocketProvider({ children = null }) {
  const { userData, storeProperties } = useContext(AuthContext);

  const storeName = _get(storeProperties, 'store_name', '');
  const ssSellerId = _get(storeProperties, 'ss_seller_id', '');
  const storeUniqueName = _get(storeProperties, 'store_unique_name', '');
  const isSSR = _get(storeProperties, 'is_ssr_retailer', false);
  const apiKey = isSSR ? SSR_API_KEY : RR_API_KEY;
  const isTrackable = apiKey && IS_PROD;

  useEffect(() => {
    if (isTrackable) {
      try {
        LogRocket.init(apiKey, {
          console: { shouldAggregateConsoleErrors: true },
          release: `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_CURRENT_GIT_SHA}`
        });
        LogRocket.getSessionURL(sessionURL => {
          Sentry.configureScope(scope => {
            scope.setExtra('sessionURL', sessionURL);
          });
        });
      } catch (error) {
        console.error('Failed to initialize LogRocket:', error);
      }
    }
  }, [apiKey, isTrackable]);

  useEffect(() => {
    const userId = _get(userData, 'userId');
    const email = _get(userData, 'email');
    const firstName = _get(userData, 'firstName');
    const lastName = _get(userData, 'lastName');

    if (isTrackable && userId) {
      try {
        LogRocket.identify(userId, {
          name: `${firstName} ${lastName}`.trim(),
          email,
          isSSR,
          storeName,
          storeUniqueName,
          sellerId: ssSellerId,
          userRole: 'SHOPPER'
        });
      } catch (error) {
        console.error('Failed to identify user in LogRocket:', error);
      }
    }
  }, [isTrackable, userData, isSSR, storeName, storeUniqueName, ssSellerId]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        LogRocket.track(eventName, eventProperties);
      } catch (error) {
        console.error('Failed to track event in LogRocket:', error);
      }
    },
    [isTrackable]
  );

  return (
    <LogRocketContext.Provider value={{ track }}>
      {children}
    </LogRocketContext.Provider>
  );
}
